import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function to get local date in YYYY-MM-DD format
const getLocalDateString = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export default function OHSCheckIn() {
  const { siteId } = useParams();
  const [siteName, setSiteName] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [companies, setCompanies] = useState([]);
  
  // Initialize form data with a function to ensure fresh date on each mount
  const [formData, setFormData] = useState(() => ({
    mobile_number: "",
    first_name: "",
    surname: "",
    contractor_company: "",
    check_in_date: getLocalDateString(), // Pre-populated with today's local date
  }));

  // Ensure date is always current when component mounts
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      check_in_date: getLocalDateString()
    }));
  }, []);

  // Load site information
  useEffect(() => {
    const loadSite = async () => {
      try {
        const response = await fetch(`${API}/sites/${siteId}`);
        if (response.ok) {
          const data = await response.json();
          setSiteName(data.name);
        }
      } catch (error) {
        console.error("Error loading site:", error);
      }
    };

    if (siteId) {
      loadSite();
    }
  }, [siteId]);

  // Load companies
  useEffect(() => {
    const loadCompanies = async () => {
      if (!siteId) return;
      
      try {
        // Load all active contractor/subcontractor companies from public endpoint
        const response = await fetch(`${API}/ohs/public-companies`);
        if (response.ok) {
          let data = await response.json();
          
          // Filter by site association
          if (siteId) {
            data = data.filter(c => 
              // Show companies with no site associations (global) OR companies associated with this site
              !c.site_ids || c.site_ids.length === 0 || c.site_ids.includes(siteId)
            );
          }
          
          setCompanies(data);
        }
      } catch (error) {
        console.error("Error loading companies:", error);
      }
    };

    loadCompanies();
  }, [siteId]);

  // Format mobile number as #### ### ###
  const formatMobileNumber = (value) => {
    const numbers = value.replace(/\D/g, "");
    if (numbers.length <= 4) {
      return numbers;
    } else if (numbers.length <= 7) {
      return `${numbers.slice(0, 4)} ${numbers.slice(4)}`;
    } else {
      return `${numbers.slice(0, 4)} ${numbers.slice(4, 7)} ${numbers.slice(7, 10)}`;
    }
  };

  // Search personnel by mobile number
  const searchPersonnel = async (mobile) => {
    if (mobile.replace(/\s/g, "").length === 10) {
      try {
        const response = await fetch(`${API}/ohs/personnel/search?mobile=${encodeURIComponent(mobile)}`);
        if (response.ok) {
          const data = await response.json();
          if (data) {
            setFormData(prev => ({
              ...prev,
              first_name: data.first_name,
              surname: data.surname,
              contractor_company: data.contractor_company,
            }));
            toast.success("Details found! Please verify and submit.");
          }
        }
      } catch (error) {
        console.error("Error searching personnel:", error);
      }
    }
  };

  const handleMobileChange = (e) => {
    const formatted = formatMobileNumber(e.target.value);
    setFormData(prev => ({ ...prev, mobile_number: formatted }));
    
    // Auto-search when 10 digits entered
    if (formatted.replace(/\s/g, "").length === 10) {
      searchPersonnel(formatted);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate company selection
    if (!formData.contractor_company) {
      toast.error("Please select a company", {
        style: {
          background: '#fee2e2',
          color: '#991b1b',
        },
      });
      return;
    }
    
    // Validate mobile number
    const mobileDigits = formData.mobile_number.replace(/\D/g, '');
    if (!formData.mobile_number || mobileDigits.length !== 10) {
      toast.error("Please enter a valid 10-digit mobile number", {
        style: {
          background: '#fee2e2',
          color: '#991b1b',
        },
      });
      return;
    }
    
    // Validate name fields
    if (!formData.first_name || !formData.surname) {
      toast.error("Please enter your first name and surname", {
        style: {
          background: '#fee2e2',
          color: '#991b1b',
        },
      });
      return;
    }
    
    setLoading(true);

    try {
      const response = await fetch(`${API}/ohs/check-in`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...formData,
          site_id: siteId,
        }),
      });

      if (response.ok) {
        setSubmitted(true);
        toast.success("Thank you for checking in!");
        
        // Close the browser window after 3 seconds
        setTimeout(() => {
          window.close();
          // If window.close() doesn't work (page wasn't opened by script),
          // reset form so it can be used again
          setTimeout(() => {
            setSubmitted(false);
            setFormData({
              mobile_number: "",
              first_name: "",
              surname: "",
              contractor_company: "",
              check_in_date: getLocalDateString(), // Reset to current local date
            });
          }, 1000);
        }, 3000);
      } else {
        const error = await response.json();
        if (response.status === 400) {
          // Duplicate check-in error - show more prominent message
          toast.error(error.detail, {
            duration: 6000,
            style: {
              background: '#fee2e2',
              color: '#991b1b',
              border: '1px solid #fca5a5',
            }
          });
        } else {
          toast.error(error.detail || "Failed to check in. Please try again.");
        }
      }
    } catch (error) {
      console.error("Error submitting check-in:", error);
      toast.error("Network error. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-8 pb-8">
            <div className="text-center space-y-4">
              <CheckCircle2 className="w-20 h-20 text-green-600 mx-auto" />
              <h2 className="text-2xl font-bold text-slate-800">Thank You!</h2>
              <p className="text-slate-600">Your attendance has been recorded successfully.</p>
              <p className="text-sm text-slate-500">Have a safe day on site!</p>
              <p className="text-xs text-slate-400 mt-4">This window will close automatically...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-amber-50 to-orange-50 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="bg-gradient-to-r from-amber-600 to-orange-600 text-white">
          <div className="flex items-center space-x-3">
            <img src="/logo.png?v=3" alt="Site Pro App" className="w-10 h-10" />
            <div>
              <CardTitle className="text-xl">OH&S Check-In</CardTitle>
              <p className="text-sm opacity-90">{siteName || "Loading site..."}</p>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="check_in_date">Date *</Label>
              <Input
                id="check_in_date"
                type="date"
                value={formData.check_in_date}
                onChange={(e) => setFormData(prev => ({ ...prev, check_in_date: e.target.value }))}
                required
                className="text-lg font-semibold"
                readOnly
              />
              <p className="text-xs text-slate-500 mt-1">Today's date (auto-filled)</p>
            </div>

            <div>
              <Label htmlFor="mobile_number">Mobile Number *</Label>
              <Input
                id="mobile_number"
                type="tel"
                placeholder="#### ### ###"
                value={formData.mobile_number}
                onChange={handleMobileChange}
                maxLength={12}
                required
                className="text-lg"
              />
              <p className="text-xs text-slate-500 mt-1">Format: 0412 345 678</p>
            </div>

            <div>
              <Label htmlFor="first_name">First Name *</Label>
              <Input
                id="first_name"
                type="text"
                value={formData.first_name}
                onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                required
              />
            </div>

            <div>
              <Label htmlFor="surname">Surname *</Label>
              <Input
                id="surname"
                type="text"
                value={formData.surname}
                onChange={(e) => setFormData(prev => ({ ...prev, surname: e.target.value }))}
                required
              />
            </div>

            <div>
              <Label htmlFor="contractor_company">Contractor Company *</Label>
              <Select
                value={formData.contractor_company}
                onValueChange={(value) => setFormData(prev => ({ ...prev, contractor_company: value }))}
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select your company" />
                </SelectTrigger>
                <SelectContent>
                  {companies.map((company) => (
                    <SelectItem key={company.id} value={company.id}>
                      {company.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-amber-700 mt-2 font-medium">
                ⚠️ If your company is not listed, please see the Site Manager to create the subcontractor attendance for the site.
              </p>
            </div>

            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <div className="flex items-start space-x-2">
                <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-blue-800">
                  <p className="font-semibold mb-1">Important:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>All fields are required</li>
                    <li>You can only check in once per day at this site</li>
                    <li>Your details will be saved for future check-ins</li>
                  </ul>
                </div>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-lg py-6"
              disabled={loading}
            >
              {loading ? "Submitting..." : "Check In"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
